import { Stack, Toggle } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { Dropdown } from '@h2oai/ui-kit';
import { useState } from 'react';

import { maxSeconds, TimeUnit, timeUnitInfoMap } from '../../../aiem/types';
import { getBestDateTimeUnitInfoPair, parseNumberAndTimeUnitToSeconds } from '../../../aiem/utils';
import SpinnerWithTooltip from '../../../components/AIEnginesPage/components/SpinnerWithTooltip/SpinnerWithTooltip';
import { DurationEntityFieldInputProps } from './BasicEntityModelComponents';
import { defaultEntityFormRowStyles, defaultEntityLabelColumnWidth } from './DefaultEntityFormRowStyles';
import { LabelAndDescription } from './LabelAndDescription';

export function DurationModelField<EntityModel>({
  field,
  model,
  onChange: onChangeModel,
  compact,
}: DurationEntityFieldInputProps<EntityModel>) {
  const { label, name, description, disabled, required } = field;
  const value = (model as any)[name];

  const [numberProp, unitProp] = getBestDateTimeUnitInfoPair(value);
  const [number, setNumber] = useState<number>(numberProp);
  const defaultUnit = TimeUnit.Second,
    [unitKey, setUnitKey] = useState<TimeUnit>(unitProp?.type || defaultUnit);
  const [enabled, { toggle }] = useBoolean(required ? true : Boolean(number));

  const options = (Object.keys(timeUnitInfoMap) as Array<keyof typeof timeUnitInfoMap>).map((key) => ({
    key,
    text: timeUnitInfoMap[key].text,
  }));

  return (
    <Stack horizontal={!compact} styles={{ root: defaultEntityFormRowStyles }} tokens={{ childrenGap: 15 }}>
      <LabelAndDescription
        label={label}
        description={description}
        rootStyles={{ minWidth: compact ? 0 : defaultEntityLabelColumnWidth }}
      />
      <Stack horizontal tokens={{ childrenGap: 17 }}>
        {!required && (
          <Stack styles={{ root: { paddingTop: 10 } }}>
            <Toggle
              disabled={disabled}
              checked={enabled}
              onChange={
                disabled
                  ? () => null
                  : (_, _checked) => {
                      toggle();
                      if (!_checked) {
                        setNumber(0);
                        setUnitKey(defaultUnit);
                        onChangeModel && onChangeModel(name, undefined);
                      }
                    }
              }
            />
          </Stack>
        )}
        <SpinnerWithTooltip
          onChange={
            disabled
              ? () => null
              : (_: any, value: number) => {
                  setNumber(value);
                  onChangeModel && onChangeModel(name, parseNumberAndTimeUnitToSeconds(value, unitKey!));
                }
          }
          value={number}
          min={0}
          max={maxSeconds}
          disabled={disabled || !enabled}
        />
        <Dropdown
          options={options}
          selectedKey={unitKey}
          width={200}
          onChange={
            disabled
              ? () => null
              : (_ev, v) => {
                  const value = v?.key as TimeUnit;
                  setUnitKey(value);
                  onChangeModel && onChangeModel(name, parseNumberAndTimeUnitToSeconds(number, value));
                }
          }
          disabled={disabled || !enabled}
        />
      </Stack>
    </Stack>
  );
}
